import $ from 'jquery'
import _ from 'lodash'
import Backbone from 'backbone'
import alertHelper from './alert-helper'
import alertMetadata from './alert-metadata'
import appDisplay from 'scoreboard/components/app-display'
import conductor from 'scoreboard/components/conductor'
import formHelper from 'scoreboard/components/form-helper'
import FormView from 'scoreboard/components/form-view'
import icon from 'scoreboard/components/icon'
import i18n from 'scoreboard/components/i18n'
import metadata from 'scoreboard/components/metadata'
import permissions from 'scoreboard/components/permissions'
import PrincipalPanel from 'scoreboard/components/principal-panel'
import PublishAlertForm from './publish-alert-form'
import user from 'scoreboard/components/user'
import template from './publish-subscribers-form.hbs'

/**
 * A dialog shown containing the current users subscription choice
 * and a list of all other subscribers.
 *
 * If the current user is a modifier of the domain object they can
 * also add/remove recipients and publish a change.
 */
const PublishSubscribersForm = Backbone.View.extend({

	tagName: 'form',

	className: 'publish-subscribers-form',

	events: {
		'change [name=subscribe]': 'onToggleSubscribe',
		'click .publish': 'onPublish'
	},

	/**
	 * @param {Number} options.orgId
	 * @param {Number} options.domainId
	 * @param {String} [options.section=conductor.section()]
	 * @param {Array} options.subscriptions
	 */
	initialize(options = {}) {
		this.options = options

		this.editable = true // Anyone can add/remove subscribers (which are filtered by permission)
		this.section = this.options.section || conductor.section()
		this.domainId = this.options.domainId
		this.domainTypeLabel = i18n.get(metadata[this.section].singularCode)
		this.subscription = {
			type: alertMetadata.types.CHANGE_PUBLISHED,
			domainId: this.domainId,
			domainType: metadata[this.section].domainType
		}

		this.subscriptions = this.options.subscriptions || []
		this._assignSubscribers()

		// Cache of subscriptionIds so we can do XHRs
		// An assumption is made that there is only one subscription per domain object, per user
		// This is enforced in AlertsDao
		this.principalsByTypeById = {'user': {}, 'group': {}}
		this.principalsByTypeById['user'][user.id] = {subscriptionId: this.userSubscriptionId}
		_.forEach(this.otherSubscribers, p => this.principalsByTypeById[p.type][p.id] = p)

		this.formView = new FormView({
			el: this.el,
			allowCleanSubmission: true,
			guard: false,
			// subscriptions used by briefing-options-menu.js
			submit: () => this.trigger('done', this.subscriptions)
		})
	},

	/**
	 * picks out the current user subscription from other users
	 */
	_assignSubscribers() {
		// getPublishChangesSubscribers adds `subscriptionId` to the principal object
		const {currentUserSubscribed, otherSubscribers} =
			alertHelper.getPublishChangesSubscribers(this.subscriptions)
		this.userSubscriptionId = currentUserSubscribed
		this.otherSubscribers = otherSubscribers
	},

	render: function () {
		this.$el.empty().append(template({
			userLabel: user.model.get('label'),
			subscribed: this.userSubscriptionId,
			otherSubscribers: _.size(this.otherSubscribers) ? this.otherSubscribers : false
		}))

		this.$('.banner').html(i18n.get('alerts.publish.description', this.domainTypeLabel))

		this.renderOtherSubscribers()
		this.secure()

		this.formView.start()

		this.togglePublishButton()

		return this
	},

	renderOtherSubscribers() {
		const readOnly = !(this.editable &&
			(permissions.localAdmin() || permissions.allGroups()) &&
			permissions.modifyUsers()) // #24943

		let params = $.param({
			modifierOnly: true,
			includeViewOnly: true,
			includeActiveViewers: true
		})
		this.otherSubscribersPanel = new PrincipalPanel({
			url: `api/organizations/${this.options.orgId}/potential-assignees` +
				`?${params}`,
			el: this.$('.principal-panel'),
			headingKey: 'alerts.subscription.others',
			placeholderKey: readOnly
				? 'alerts.subscription.noOtherSubscribers'
				: 'admin.group.addUserGroup',
			currentPrincipals: this.otherSubscribers,
			readOnly
		}).render()

		const text = _.escape(i18n.get('common.publish.withArg', this.domainTypeLabel))
		const classes = `class="publish button button-covert-labeled"`
		this.$('th.table-actions-cell').append(`<a ${classes}>${icon('send-to')}${text}</a>`)

		this.otherSubscribersPanel.on('change:add', principal => this._sendXHR('post', principal))
		this.otherSubscribersPanel.on('change:delete', principal =>  this._sendXHR('delete', principal))
	},

	secure: function() {
		if (!_.invoke(permissions, this.section, this.options.orgId)) {
			this.$('.publish').addClass('is-not-displayed')
		}

		// Remove delete buttons for principals the current user can not modify
		const byId = _.keyBy(this.subscriptions, 'id')
		this.otherSubscribersPanel.getRows().each((i, trEl) => {
			const tr = $(trEl)
			const {id, type} = tr.data()
			if (id && type) {
				const {subscriptionId} = this.principalsByTypeById[type][id]
				if (!byId[subscriptionId].deletable) {
					tr.find('.table-actions').remove()
				}
			}
		})
	},


	/**
	 * Subscribe or Unsubscribe the current user (toggling checkbox)
	 */
	onToggleSubscribe(e) {
		const {subscribe} = formHelper.readFromDom(this.el)
		this._sendXHR(subscribe ? 'post' : 'delete', user.principal)
		this.togglePublishButton()
	},

	onPublish(e) {
		appDisplay.dialog(new PublishAlertForm({
			domainTypeLabel: this.domainTypeLabel,
			subscription: this.subscription,
			recipientCount: _.size(this.subscriptions)
		}))
	},

	// If current user can publish, the publish button is always displayed. But if neither the current user,
	// nor other users are subscribed, the button should be disabled.
	togglePublishButton() {
		let hasOtherSubscribers = _.size(this.otherSubscribers) > 0
		let currentUserIsSubscribed = this.$('[name=subscribe]').is(':checked')
		this.$('.publish').toggleClass('is-disabled', !hasOtherSubscribers && !currentUserIsSubscribed)
	},

	/**
	 * POST or DELETE a subscription
	 * @param {String} method 'post' or 'delete'
	 * @param {Principal.java} principal
	 */
	_sendXHR(method, principal) {
		const isDelete = method === 'delete'

		// Null: Delete, Undefined: Get, Defined: Set
		const subscriptionForPrincipal = (sub) => {
			const {id,type} = principal
			if (_.isNull(sub)) {
				delete this.principalsByTypeById[type][id]
			} else if (_.isUndefined(sub)) {
				return this.principalsByTypeById[type][id] || {}
			} else {
				this.principalsByTypeById[type][id] = {id, type, subscriptionId: sub.id}
			}
		}

		const {subscriptionId} = subscriptionForPrincipal()
		const subscriptionData = _.extend({owningPrincipal: principal}, this.subscription)
		let url = `api/alerts/subscriptions`

		if (isDelete) {
			subscriptionData.id = subscriptionId,
			url += `/${subscriptionId}`
		}

		return $.ajax({method, url, data: JSON.stringify(subscriptionData)})
			.then(newSubscriptionId => {
				if (isDelete) {
					_.remove(this.subscriptions, {id: subscriptionId})
					subscriptionForPrincipal(null)
					this.togglePublishButton()
				} else {
					const newSubscription = _.extend({
						id: newSubscriptionId,
						owningPrincipal: principal,
						deletable: true // This user just created it, so they can also delete it
					}, this.subscription)
					subscriptionForPrincipal(newSubscription)
					this.subscriptions.push(newSubscription)
				}
				this._assignSubscribers()
				this.togglePublishButton()
				this.trigger('change:subscriptions', this.subscriptions)
		})
	}

})

export default PublishSubscribersForm
PublishSubscribersForm.__filename__ = 'alerts/publish-subscribers-form.js'
